import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';

import { cn } from '@/lib/utils';

/* ============================================================
   단추 — shadcn 기본값을 우리 자에 맞춰 고쳤다
   ------------------------------------------------------------
   **받아온 그대로 쓰지 않는다.** 어긋난 곳이 셋이었다.

   1. **굵기 `font-medium`(500)** — Windows 맑은고딕에 **아예 없는 굵기**다.
      확정 규칙이 400/700 둘뿐이라 전부 `font-bold`/`font-normal` 로 바꿨다.
   2. **글자·간격이 Tailwind 기본 스케일**(`text-sm`·`px-4`) — 우리 자
      (`text-body`·`px-s4`)로 갈아 끼웠다. 값은 같지만 이름이 우리 것이라야
      나중에 자를 옮길 때 한 곳만 고치면 된다.
   3. **포커스 표시가 `ring`** — 이 앱은 `:focus-visible` 에 2px 외곽선을
      전역으로 걸어 뒀다. shadcn 의 `outline-none` 이 그것을 죽이면 옮긴
      화면과 안 옮긴 화면의 포커스 모양이 달라진다. **ring 을 걷고 전역
      외곽선을 그대로 쓴다.**

   높이 38px·32px 은 간격 자 밖의 숫자다 — 이건 조작 요소의 치수이고
   원래 `.btn`·`.btn-out` 이 쓰던 값 그대로다.

   **좁은 화면 44px 은 여기서 진다.** `styles.css` 가 `@media (max-width:720px)`
   에서 `.btn`·`.btn-out`·`.btn-s`·`.ib` 를 44px 로 올린다 — 손가락이 닿는
   크기다(실측: 375px 에서 로그아웃 67×32, 설정 34×34 였다. 기준은 44×44).
   `.btn` 을 걷으면 그 규격이 **조용히 사라진다.** 화면마다 손으로 되돌리면
   빠뜨리는 자리가 생기므로 단추가 스스로 갖게 했다.

   ⚠ 넓은 화면은 안 키운다. 마우스가 짚는 자리라 지금 크기가 맞고, 다 키우면
   화면이 성겨진다.
   ============================================================ */

const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-s2 rounded-md whitespace-nowrap transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /** 주 동작. 옛 `.btn` */
        default: 'bg-primary text-primary-foreground font-bold shadow-panel hover:bg-brand-d',
        /** 보조 동작. 옛 `.btn-ghost` — 흰 판에 실선 하나 */
        ghost:
          'bg-panel text-ink-2 font-normal border border-line hover:bg-canvas hover:text-ink disabled:hover:bg-panel disabled:hover:text-ink-2',
        /** 상단바처럼 색 있는 바탕 위에 놓이는 단추. 옛 `.btn-out` */
        outline:
          'bg-panel text-ink-2 font-normal border border-nav-line hover:bg-nav-2 hover:text-ink hover:border-ink-2',
        /** 되돌릴 수 없는 동작에만. 색은 상태 자에서 온다 */
        destructive: 'bg-st-red-f text-panel font-bold shadow-panel hover:opacity-90',
        /** 글자만. 표 안의 부수 동작 */
        link: 'text-brand font-normal underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-[38px] px-s4 text-body max-[720px]:h-[44px]',
        sm: 'h-8 px-s3 text-small max-[720px]:h-[44px]',
        icon: 'size-[38px] max-[720px]:size-[44px]',
        'icon-sm': 'size-8 max-[720px]:size-[44px]',
      },
      /** 폼 안에서 칸을 꽉 채운다. 옛 `.btn-block` */
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'default', size: 'default', block: false },
  },
);

function Button({
  className,
  variant,
  size,
  block,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, block, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
